import React, { useEffect, useState, useRef } from 'react';
import {
  getAllSearchedPatients,
  getPatientDetailsApi,
} from '../../../api/patientApi';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { ClinicalCustomerProfile } from './ClinicalCustomerProfile';
import { AndrologyCustomerProfile } from '../andrology/components/AndrologyCustomerProfile';
import { trimString } from '../../../utils/common';
import { resetAndrologyData } from '../../../actions/andrologyAction';
import { resetPatientData, setSelectedPatientData } from '../../../actions';
import { useDispatch, useSelector } from 'react-redux';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { resetEmbryologyData } from '../../../actions/embryologyActions';
import { EmbryologyProfile } from '../Embryology/EmbryologyProfile';

const SearchForm = ({ andrology, patientDataHandler, embryology }) => {
  const [searchKey, setSearchKey] = useState('pid');
  const [searchValue, setSearchValue] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [intermediateData, setIntermediateData] = useState(null);
  const [open, setOpen] = useState(false);
  const autoCompleteRef = useRef(null);
  const selectedEmbrologyPatientData = useSelector(
    (state) => state.embryologyReducer.selectedPatientData,
  );

  const selectedPatientData = useSelector(
    (state) => state.andrologyReducer.selectedPatientData,
  );

  const selectedClinicalPatientData = useSelector(
    (state) => state.clinicalReducer.selectedPatientData,
  );

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleSearchChange = (e) => {
    setSearchValue(e.target.value);
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    dispatch(resetEmbryologyData());
    dispatch(resetAndrologyData());
    dispatch(resetPatientData());
    setSearching(true);
    const trimmedSearchValue = trimString(searchValue);
    setSearchValue(trimmedSearchValue);
    try {
      if (!trimmedSearchValue) {
        toast.error('Please enter search field');
        setSearching(false);
        return;
      }
      const patients = await getAllSearchedPatients({
        searchValue: trimmedSearchValue,
        page: 1,
        pageSize: 10,
        searchByPhone: searchKey === 'phone',
        searchByPatientId: searchKey === 'pid',
        searchByAadhaarNumber: searchKey === 'aadhar',
        searchByName: searchKey === 'name',
        searchByPartner: andrology,
      });
      const fetchedPatients = patients.data.patients;
      if (fetchedPatients.length <= 0) {
        toast.error('No patients found');
        setSearching(false);
        return;
      }
      if (fetchedPatients.length === 1) {
        const intermediate = await getPatientDetailsApi(fetchedPatients[0].id);
        const targetPatient = intermediate.data.patient;
        if (andrology || embryology) {
          patientDataHandler(intermediate.data);
          console.log('handle search called');
        } else {
          // setIntermediateData(targetPatient);
          dispatch(setSelectedPatientData(intermediate.data));
        }
        setSearching(false);
        return;
      }

      setSearchResults(fetchedPatients);
      setOpen(true);
      autoCompleteRef.current.focus();
    } catch (err) {
      console.error(err);
      toast.error('Got error while fetching');
    }
    setSearching(false);
  };

  const handleSelectPatient = async (event, value) => {
    const selectedPatient = searchResults.find((patient) => {
      if (andrology) {
        switch (searchKey) {
          case 'name':
            return (
              `${patient.partner.firstName} ${patient.partner.lastName}` ===
              value
            );
          case 'phone':
            return patient.partner.phone === value;
          case 'aadhar':
            return patient.partner.adharNumber === value;
          case 'pid':
          default:
            return patient.partner.upid;
        }
      } else {
        switch (searchKey) {
          case 'pid':
            return patient.upid === value;
          case 'phone':
            return patient.phone === value;
          case 'name':
            return `${patient.firstName} ${patient.lastName}` === value;
          case 'aadhar':
            return patient.adharNumber === value;
          default:
            return false;
        }
      }
    });

    if (selectedPatient) {
      try {
        const intermediate = await getPatientDetailsApi(selectedPatient.id);
        const targetPatient = intermediate.data.patient;

        if (andrology || embryology) {
          patientDataHandler(intermediate.data);
          console.log('handle search called');
        } else {
          // setIntermediateData(targetPatient);
          dispatch(setSelectedPatientData(intermediate.data));
        }
      } catch (err) {
        console.error(err);
        toast.error('Got error while fetching patient details');
      }
    }
  };

  useEffect(() => {
    if (!searchValue) {
      if (!selectedEmbrologyPatientData) {
        dispatch(resetEmbryologyData());
      }
      dispatch(resetAndrologyData());
    }
  }, [searchValue, dispatch]);

  const getOptions = (searchResults, searchKey, andrology) => {
    return searchResults.slice(0, 10).map((option) => {
      if (andrology) {
        switch (searchKey) {
          case 'name':
            return `${option.partner.firstName} ${option.partner.lastName}`;
          case 'phone':
            return option.partner.phone;
          case 'aadhar':
            return option.partner.adharNumber;
          case 'pid':
          default:
            return option.upid;
        }
      } else {
        switch (searchKey) {
          case 'name':
            return `${option.firstName} ${option.lastName}`;
          case 'phone':
            return option.phone;
          case 'aadhar':
            return option.adharNumber;
          case 'pid':
          default:
            return option.upid;
        }
      }
    });
  };

  const handleCancel = () => {
    setSearchResults([]);
    dispatch(resetAndrologyData());
    dispatch(resetEmbryologyData());
    dispatch(resetPatientData());
    // setIntermediateData(null);
    setSearchValue('');
    setSearchKey('pid');
    setOpen(false);
  };

  return (
    <>
      <div className="search-container">
        <div style={{ display: 'flex', flexDirection: 'row-reverse' }}>
          <span>
            <button
              style={{ backgroundColor: 'white', color: 'black' }}
              onClick={handleCancel}
              type="button"
            >
              <i className="fa fa-close"></i>
            </button>
          </span>
        </div>
        {(!(andrology || embryology) ||
          !(
            (andrology && selectedPatientData.isFetched) ||
            (embryology && selectedEmbrologyPatientData.isFetched)
          )) && (
          <form
            onSubmit={handleSearch}
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <div className="search-wrapper">
              <select
                disabled={searching}
                className="col-md-5 custom-select"
                value={searchKey}
                onChange={(e) => {
                  setSearchResults([]);
                  dispatch(resetAndrologyData());
                  // setIntermediateData(null);
                  dispatch(resetPatientData());
                  setSearchValue('');
                  setSearchKey(e.target.value);
                }}
                required
              >
                <option value="pid">Search By Patient ID</option>
                <option value="phone">Search By Phone</option>
                <option value="name">Search By Name</option>
                <option value="aadhar">Search By Aadhar</option>
              </select>

              <div className="autocomplete-wrapper">
                <Autocomplete
                  freeSolo
                  id="free-solo-2-demo"
                  disableClearable
                  options={getOptions(
                    searchResults.slice(0, 10),
                    searchKey,
                    andrology,
                    embryology,
                  )}
                  value={searchValue}
                  onChange={handleSelectPatient}
                  open={open}
                  onOpen={() => setOpen(true)}
                  onClose={() => setOpen(false)}
                  // renderOption={(props, option) => (
                  //   <li {...props}>
                  //     <span>{option}</span>
                  //   </li>
                  // )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      inputRef={autoCompleteRef}
                      label={`Enter ${(() => {
                        switch (searchKey) {
                          case 'pid':
                            return 'Patient ID';
                          case 'phone':
                            return 'Phone Number';
                          case 'name':
                            return 'Name';
                          case 'aadhar':
                            return 'Aadhar Number';
                          default:
                            return 'Patient ID';
                        }
                      })()}`}
                      slotProps={{
                        input: {
                          ...params.InputProps,
                          type: 'search',
                        },
                      }}
                      onChange={handleSearchChange}
                    />
                  )}
                />
                <button type="submit">Search</button>
              </div>
            </div>
          </form>
        )}
        {
          <>
            {selectedClinicalPatientData &&
            selectedClinicalPatientData.isFetched &&
            !andrology ? (
              <ClinicalCustomerProfile
                intermediateData={selectedClinicalPatientData.patient}
                navigate={navigate}
              />
            ) : embryology && selectedEmbrologyPatientData.isFetched ? (
              <EmbryologyProfile />
            ) : (
              selectedPatientData &&
              selectedPatientData.isFetched && <AndrologyCustomerProfile />
            )}
          </>
        }
      </div>
    </>
  );
};

export default SearchForm;
